/**
 * Application identity on the Tame Dynamics platform.
 *
 * The slug is the single value from which the public base path, the API
 * namespace, the database table prefix and every object-storage prefix are
 * derived. Changing `VITE_APP_SLUG` relocates the entire application
 * consistently - nothing else needs editing.
 */

import { env } from './env.js';

const slug = env.appSlug;

/** `ircf` → `ircf`, matching the Prisma `@@map` table prefix. */
const tablePrefix = `${slug.replace(/-/g, '_')}_`;

export const app = {
  /** Client application slug, e.g. `ircf`. */
  slug,

  /** Display name used in `<title>`, emails and the UI chrome. */
  name: env.appName,

  /** Environment mode. */
  mode: env.mode,
  isDevelopment: env.isDevelopment,
  isProductionBuild: env.isProductionBuild,

  /** Platform API root, without the client namespace. */
  apiRootUrl: env.apiBaseUrl,

  /** Fully namespaced API base for this application. */
  apiBaseUrl: `${env.apiBaseUrl}/${slug}`,

  /** Path prefix this SPA is mounted at; IRCF production uses `/`. */
  basePath: env.basePath,

  /** Absolute public origin including base path. */
  publicSiteUrl: env.publicSiteUrl,

  /** Prisma/PostgreSQL table prefix for this application's isolated models. */
  tablePrefix,

  /** Host filesystem directory the built `dist/` is published to. */
  deployDirectory: `/opt/tame-platform/${slug}`,

  /** Read-only mount point inside the Caddy container. */
  caddyRoot: `/srv/${slug}`,

  locale: env.locale,
  timezone: env.timezone,
  currency: env.currency,
} as const;

/** Object-storage key prefixes. Public and private are strictly separated. */
export const storagePrefixes = {
  public: `clients/${slug}/public/`,
  privateRoot: `clients/${slug}/private/`,
  clientProgress: (clientId: string) => `clients/${slug}/private/clients/${clientId}/progress/`,
  clientDocuments: (clientId: string) => `clients/${slug}/private/clients/${clientId}/documents/`,
  clientHealth: (clientId: string) => `clients/${slug}/private/clients/${clientId}/health/`,
  messages: `clients/${slug}/private/messages/`,
  checkIns: `clients/${slug}/private/check-ins/`,
} as const;

/**
 * Joins a path onto the SPA base path. Use for anything that must survive the
 * nested mount point: `<link>` hrefs, manifest URLs, canonical URLs, redirects
 * issued outside React Router.
 */
export function withBasePath(path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${app.basePath}${suffix}`;
}

/** Absolute public URL for a route, used for canonical tags and Open Graph. */
export function absoluteUrl(path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${app.publicSiteUrl}${suffix === '/' ? '/' : suffix}`;
}
